import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Users,
  Trophy,
  Settings,
  ChevronRight,
  CheckCircle2,
  FileText,
  Star,
  Ban,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSeries } from '../../App';
import { useApi } from '../../contexts/ApiContext';

const checklistItemsConfig = [
  { id: 'seasonInfo', label: 'Season Information', path: 'config/season' },
  { id: 'preloadSeries', label: 'Preload Series', path: 'config/series' },
  { id: 'preloadClasses', label: 'Preload Classes', path: 'config/classes' },
  { id: 'eventsConfigured', label: 'Events Configured', path: 'events' },
  { id: 'pointsConfiguration', label: 'Points Configuration', path: 'config/points' },
  { id: 'paymentsSetup', label: 'Payments Setup', path: 'config/payments' },
  { id: 'fantasyIntegration', label: 'Fantasy Integration', path: 'config/fantasy' },
];

const quickActions = [
  { label: 'Events', icon: Calendar, path: 'events', color: 'text-wrg-blue-600' },
  { label: 'Points', icon: Trophy, path: 'points', color: 'text-amber-500' },
  { label: 'Members', icon: Users, path: 'memberships', color: 'text-green-500' },
  { label: 'Suspended', icon: Ban, path: 'suspensions', color: 'text-red-500' },
  { label: 'Reports', icon: FileText, path: 'reports', color: 'text-purple-500' },
  { label: 'Settings', icon: Settings, path: 'settings', color: 'text-slate-500' },
];

const getEventStatus = (statusId, entryCount) => {
  if (statusId === 2 || statusId === 3) return 'ready';
  if (entryCount > 0) return 'setup';
  return 'pending';
};

const formatEventDate = (dateString) => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export default function SeasonDashboard() {
  const { seriesSlug } = useParams();
  const currentSeries = useSeries();
  const api = useApi();

  const series = currentSeries || {
    name: 'Unknown Series',
    color: '#3b5998',
    logo: null
  };

  const seriesId = currentSeries?.dbId || currentSeries?.racingEntityId;

  const { data: upcomingEvents = [], isLoading: eventsLoading, error: eventsError } = useQuery({
    queryKey: ['upcoming-events', seriesId],
    queryFn: async () => {
      const result = await api.events.getAll({
        series_id: seriesId,
        upcoming: 'true',
        limit: 5,
        sort: 'event_date',
        order: 'asc'
      });
      if (!result.success || !result.data?.events) throw new Error(result.message || 'Failed to load events');
      return result.data.events;
    },
    enabled: !!seriesId,
  });

  const { data: seasonData, isLoading: seasonLoading } = useQuery({
    queryKey: ['season-dashboard', currentSeries?.dbId],
    queryFn: async () => {
      const result = await api.series.getSeasonDashboard(currentSeries.dbId);
      if (!result.success || !result.data) throw new Error(result.message || 'Failed to load season dashboard');
      return result.data;
    },
    enabled: !!currentSeries?.dbId,
  });

  const checklist = seasonData?.checklist || {};
  const checklistItems = checklistItemsConfig.map(item => ({
    ...item,
    completed: checklist[item.id] || false
  }));
  const completedItems = checklistItems.filter(item => item.completed).length;
  const totalItems = checklistItems.length;
  const completionPercent = Math.round((completedItems / totalItems) * 100);

  const stats = seasonData?.stats || {};
  const pointsLeader = seasonData?.pointsLeader;
  const currentYear = seasonData?.year || new Date().getFullYear();

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          {series.logo ? (
            <img
              src={series.logo}
              alt={series.name}
              className="w-10 h-10 object-contain"
            />
          ) : (
            <div
              className="w-10 h-10 rounded flex items-center justify-center"
              style={{ backgroundColor: `${series.color}20` }}
            >
              <div
                className="w-6 h-6 rounded"
                style={{ backgroundColor: series.color }}
              />
            </div>
          )}
          <div>
            <h1 className="page-title">{series.name}</h1>
            <p className="page-subtitle">{currentYear} Season Management</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Season Setup Checklist */}
          <Card padding="compact">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Season Setup Checklist</CardTitle>
                  <CardDescription>Complete these steps to prepare for the season</CardDescription>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-slate-800">{completionPercent}%</span>
                  <p className="text-2xs text-slate-500">{completedItems}/{totalItems} complete</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Progress bar */}
              <div className="w-full h-1.5 bg-slate-200 rounded-full mb-3 overflow-hidden">
                <div
                  className="h-full bg-wrg-blue-600 rounded-full transition-all duration-500"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>

              <div className="space-y-1">
                {checklistItems.map((item) => (
                  <Link
                    key={item.id}
                    to={`/${seriesSlug}/${item.path}`}
                    className="flex items-center justify-between p-2 rounded hover:bg-slate-50 transition-colors group"
                  >
                    <div className="flex items-center gap-2.5">
                      {item.completed ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                      )}
                      <span className={`text-sm ${item.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                        {item.label}
                      </span>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-colors" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card padding="compact">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Upcoming Events</CardTitle>
                  <CardDescription>Next events on the schedule</CardDescription>
                </div>
                <Link to={`/${seriesSlug}/events`}>
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {eventsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : eventsError ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <AlertCircle className="w-6 h-6 text-red-400 mb-1" />
                  <p className="text-xs text-slate-500">{eventsError?.message}</p>
                </div>
              ) : upcomingEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <Calendar className="w-6 h-6 text-slate-300 mb-1" />
                  <p className="text-xs text-slate-500">No upcoming events scheduled</p>
                  <Link to={`/${seriesSlug}/events/new`} className="mt-2">
                    <Button variant="secondary" size="sm">Add Event</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {upcomingEvents.map((event) => {
                    const status = getEventStatus(event.status_id, event.entry_count);
                    return (
                      <Link
                        key={event.id}
                        to={`/${seriesSlug}/events/${event.id}`}
                        className="flex items-center justify-between p-3 rounded bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-slate-800 group-hover:text-wrg-blue-600 transition-colors">
                              {event.name}
                            </h4>
                            <p className="text-xs text-slate-500">
                              {formatEventDate(event.event_date)}
                              {event.track_name && ` • ${event.track_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge
                            variant={
                              status === 'ready' ? 'success' :
                              status === 'setup' ? 'warning' : 'neutral'
                            }
                            dot
                          >
                            {status === 'ready' ? 'Ready' :
                             status === 'setup' ? 'In Setup' : 'Pending'}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {event.entry_count || 0} entries
                          </span>
                          <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Quick Actions & Stats */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <Card padding="compact">
            <CardHeader border={false}>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2">
                {quickActions.map((action) => (
                  <Link
                    key={action.label}
                    to={`/${seriesSlug}/${action.path}`}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors group"
                  >
                    <action.icon className={`w-5 h-5 ${action.color}`} />
                    <span className="text-2xs text-slate-600 group-hover:text-slate-800 text-center">
                      {action.label}
                    </span>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Season Stats */}
          <Card padding="compact">
            <CardHeader border={false}>
              <CardTitle>Season Stats</CardTitle>
            </CardHeader>
            <CardContent>
              {seasonLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2.5 rounded bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2.5">
                      <Calendar className="w-4 h-4 text-wrg-blue-600" />
                      <span className="text-xs text-slate-600">Total Events</span>
                    </div>
                    <span className="text-md font-bold text-slate-800">{stats.totalEvents || 0}</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 rounded bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2.5">
                      <Users className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-slate-600">Registered Drivers</span>
                    </div>
                    <span className="text-md font-bold text-slate-800">{stats.registeredDrivers || 0}</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 rounded bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2.5">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      <span className="text-xs text-slate-600">Races Completed</span>
                    </div>
                    <span className="text-md font-bold text-slate-800">{stats.completedRaces || 0}</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 rounded bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2.5">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-slate-600">Unique Drivers</span>
                    </div>
                    <span className="text-md font-bold text-slate-800">{stats.uniqueDrivers || 0}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Points Leader */}
          <Card padding="compact">
            <CardHeader border={false}>
              <CardTitle>Points Leader</CardTitle>
            </CardHeader>
            <CardContent>
              {seasonLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : pointsLeader ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">
                      {pointsLeader.firstName} {pointsLeader.lastName}
                    </h4>
                    <p className="text-xs text-slate-500">
                      {pointsLeader.wins} wins • {pointsLeader.racesEntered} races
                    </p>
                    <p className="text-md font-bold text-amber-600 mt-0.5">
                      {pointsLeader.totalPoints?.toLocaleString()} pts
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-4 text-center">
                  <Trophy className="w-6 h-6 text-slate-300 mb-1" />
                  <p className="text-xs text-slate-500">No points data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
